import { useCallback, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import { useFeatureFlags } from '../../contexts/featureFlag/useFeatureFlags';
import ButtonWithArrowIcon from 'components/Buttons/ButtonWithArrowIcon';
import { ReactComponent as CloseIcon } from 'assets/images/close-dialog.svg';
import { CropImage } from 'components/CropImage';
import { changeImageOrientation, getImageDimensions } from 'functions/images';
import styles from './styles.module.scss';

const aspectRatios = {
  card: 1.5,
  document: 1.2,
  square: 1,
};

type InputProps = {
  placeholder: string;
  uploadedFileUrl: string;
  onChange: (file?: File, imagePreview?: string) => void;
  onInEditing?: (isEditing: boolean) => void;
  onClick?: () => void;
  aspectRatio?: 'card' | 'document' | 'square';
  noEditStep?: boolean;
};

export const UploadImage = (
  {
    placeholder,
    onChange,
    onClick,
    aspectRatio = 'card',
    ...props
  }: InputProps,
  ref: any
) => {
  const inputFile: any = useRef(null);
  const [currentImage, setCurrentImage] = useState<File | Blob | undefined>();
  const [isEditing, setIsEditing] = useState(false);
  const [localUploadedFileUrl, setLocalUploadedFileUrl] = useState<string>();
  const [isPdfDisplay, setIsPdfDisplay] = useState(false);
  const { featureFlags } = useFeatureFlags();

  const allowFiles = useMemo(() => {
    const allowFiles = ['image/jpg', 'image/jpeg', 'image/png'];
    if (featureFlags?.['allow-pdf-upload']) {
      allowFiles.push('application/pdf');
    }
    return allowFiles.join(', ');
  }, [featureFlags]);

  const currentImageUrl = useMemo(() => {
    return currentImage ? URL.createObjectURL(currentImage) : '';
  }, [currentImage]);

  const handleChange = async (e: any) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.type === 'application/pdf') {
      processIfPDF(file);
    } else {
      processIfImage(file);
    }
  };

  const processIfPDF = (file: File) => {
    setIsPdfDisplay(true);
    onChange(file);
  };

  const processIfImage = async (file: File) => {
    setIsPdfDisplay(false);
    const fileUrl = URL.createObjectURL(file);

    setCurrentImage(file);

    if (props.noEditStep) {
      const imgDimentions = await getImageDimensions(fileUrl);
      if (imgDimentions.orientation === 'portrait') {
        const rotatedImage = await changeImageOrientation(fileUrl);
        setLocalUploadedFileUrl(rotatedImage);
        onChange(file, rotatedImage);
      } else {
        setLocalUploadedFileUrl(fileUrl);
        onChange(file);
      }
    } else {
      setLocalUploadedFileUrl(fileUrl);
      setIsEditing(true);
      props.onInEditing && props.onInEditing(true);
    }
  };

  const onClickHandler = useCallback(() => {
    if (onClick) {
      onClick();
    } else {
      if ('current' in ref) {
        ref.current.click();
        ref.current.value = '';
      } else {
        inputFile.current.click();
        inputFile.current.value = '';
      }
    }
  }, [ref]);

  const onCancel = useCallback((e: any) => {
    e.stopPropagation();
    e.preventDefault();
    onChange(undefined);
    setLocalUploadedFileUrl(undefined);
    setIsEditing(false);
    props.onInEditing && props.onInEditing(false);
  }, []);

  const onConfirmCropImage = (file: File | Blob) => {
    setIsEditing(false);
    props.onInEditing && props.onInEditing(false);
    onChange(file as File);
  };

  return (
    <>
      {isEditing && currentImageUrl && (
        <CropImage
          fileUrl={currentImageUrl}
          aspectRatio={aspectRatios[aspectRatio]}
          onSave={onConfirmCropImage}
        />
      )}
      {!isEditing && (
        <div
          className={clsx(styles.root, localUploadedFileUrl && styles.uploaded)}
          onClick={onClickHandler}
        >
          <div
            className={clsx(
              styles.uploadArea,
              styles[`aspectRatio_${aspectRatio}`],
              {
                [styles.uploadedContainer]:
                  !!localUploadedFileUrl && styles.uploadedContainer,
                [styles.noEditStep]: !!localUploadedFileUrl && props.noEditStep,
              }
            )}
          >
            {localUploadedFileUrl && !isPdfDisplay && (
              <>
                <img
                  src={localUploadedFileUrl}
                  alt={placeholder}
                  className={clsx(styles.uploaded, {
                    [styles.noEditStep]: props.noEditStep,
                  })}
                />
                <div className={styles.closeIcon} onClick={onCancel}>
                  <CloseIcon />
                </div>
              </>
            )}
            {!localUploadedFileUrl && (
              <>
                <ButtonWithArrowIcon variant="contained" color="primary">
                  Select Image
                </ButtonWithArrowIcon>
              </>
            )}
            <input
              onChange={handleChange}
              ref={'current' in ref ? ref : inputFile}
              accept={allowFiles}
              type="file"
            />
          </div>
        </div>
      )}
    </>
  );
};
